import { useEffect, useState, useCallback, useRef } from 'react'
import { useToast } from '@/hooks/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { apiClient } from '@/lib/api'

const INACTIVITY_TIMEOUT = 4 * 60 * 60 * 1000 // 4 hours
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000 // 5 minutes warning
// Activity is shared across tabs: apiClient.logout() clears the (shared)
// localStorage session AND nulls the server-side refresh token, so an idle
// background tab firing this timer used to evict the user from the tab they
// were actively working in. Every tab records activity here; the timer only
// logs out when NO tab has seen activity within the window.
const LAST_ACTIVITY_KEY = 'gracklecrm_last_activity'

function readSharedActivity(): number {
  const raw = Number(localStorage.getItem(LAST_ACTIVITY_KEY))
  return Number.isFinite(raw) && raw > 0 ? raw : 0
}

/**
 * Hook that monitors user activity and logs out after 4 hours of inactivity.
 * Shows a warning toast 5 minutes before automatic logout.
 */
export function useInactivityTimeout() {
  const { toast } = useToast()
  const [lastActivity, setLastActivity] = useState(() => Math.max(readSharedActivity(), Date.now()))
  const [warningShown, setWarningShown] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetActivity = useCallback(() => {
    const now = Date.now()
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    } catch {
      // Private mode / quota — fall back to this tab's own timer.
    }
    setLastActivity(now)
    setWarningShown(false)
  }, [])

  const logout = useCallback(() => {
    // Final cross-tab check: another tab may have been active while this one
    // sat idle. Only end the session when every tab has been quiet.
    const shared = readSharedActivity()
    if (shared && Date.now() - shared < INACTIVITY_TIMEOUT) {
      setLastActivity(shared)
      setWarningShown(false)
      return
    }
    toast({
      title: 'Session Ended',
      description: 'You have been logged out due to inactivity.',
      variant: 'destructive',
    })
    apiClient.logout()
  }, [toast])

  const showWarning = useCallback(() => {
    if (warningShown) return
    setWarningShown(true)

    toast({
      title: 'Inactivity Warning',
      description: 'You will be logged out in 5 minutes due to inactivity.',
      action: (
        <ToastAction altText="Stay logged in" onClick={resetActivity}>
          Stay Logged In
        </ToastAction>
      ),
      duration: WARNING_BEFORE_LOGOUT,
    })
  }, [toast, warningShown, resetActivity])

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

    const handleActivity = () => {
      resetActivity()
    }

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Pick up activity happening in other tabs so this tab's timer stays in sync.
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== LAST_ACTIVITY_KEY || !e.newValue) return
      const ts = Number(e.newValue)
      if (Number.isFinite(ts) && ts > 0) {
        setLastActivity(ts)
        setWarningShown(false)
      }
    }
    window.addEventListener('storage', handleStorage)

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity)
      })
      window.removeEventListener('storage', handleStorage)
    }
  }, [resetActivity])

  // Check inactivity and manage timeouts
  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (!token) return // Don't run if not logged in

    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (warningRef.current) clearTimeout(warningRef.current)

    const timeUntilWarning = INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT
    const timeSinceActivity = Date.now() - lastActivity
    const remainingUntilWarning = timeUntilWarning - timeSinceActivity
    const remainingUntilLogout = INACTIVITY_TIMEOUT - timeSinceActivity

    if (remainingUntilLogout <= 0) {
      logout()
    } else if (remainingUntilWarning <= 0) {
      showWarning()
      timeoutRef.current = setTimeout(logout, remainingUntilLogout)
    } else {
      warningRef.current = setTimeout(showWarning, remainingUntilWarning)
      timeoutRef.current = setTimeout(logout, remainingUntilLogout)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (warningRef.current) clearTimeout(warningRef.current)
    }
  }, [lastActivity, logout, showWarning])

  return { resetActivity, lastActivity }
}
